/**
 * DisposalFormModal — Phase 24 / Task 24.3 (DEC-094).
 *
 *   mode 'authorize' : confirm a picked SWD/INJ well + optional hauler display name.
 *   mode 'edit'      : edit the display name, or deactivate the authorization.
 */
import * as React from 'react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { fieldStyle, labelStyle, FormRow } from './form-bits';
import {
  useAuthorizeDisposal,
  useUpdateDisposal,
  useDeactivateDisposal,
  type DisposalFacility,
  type WellResult,
} from './hooks';
import { Pill } from './ui-bits';

export interface DisposalFormModalProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly mode: 'authorize' | 'edit';
  readonly well?: WellResult | null;
  readonly facility?: DisposalFacility | null;
  readonly canManage: boolean;
}

export function DisposalFormModal({
  open,
  onOpenChange,
  mode,
  well,
  facility,
  canManage,
}: DisposalFormModalProps): React.JSX.Element {
  const [displayName, setDisplayName] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setDisplayName(mode === 'edit' ? (facility?.displayName ?? '') : '');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, facility?.id]);

  const close = (): void => onOpenChange(false);
  const authorize = useAuthorizeDisposal(() => {
    toast.success('Disposal well authorized');
    close();
  });
  const update = useUpdateDisposal(facility?.id ?? '');
  const deactivate = useDeactivateDisposal(facility?.id ?? '');
  const pending = authorize.isPending || update.isPending || deactivate.isPending;

  const handleSave = (): void => {
    const trimmed = displayName.trim();
    if (mode === 'authorize') {
      if (well == null) return;
      authorize.mutate(
        { wellId: well.id, ...(trimmed === '' ? {} : { displayName: trimmed }) },
        { onError: () => setError('Could not authorize — it may already be authorized.') },
      );
      return;
    }
    update.mutate(
      { displayName: trimmed === '' ? null : trimmed },
      {
        onSuccess: () => {
          toast.success('Display name updated');
          close();
        },
        onError: () => setError('Could not update. Try again.'),
      },
    );
  };

  const catalogLabel =
    mode === 'authorize'
      ? well != null
        ? `${well.wellName} · API ${well.apiNumber}`
        : ''
      : facility != null
        ? `${facility.wellName} · API ${facility.apiNumber}`
        : '';
  const wellType = mode === 'authorize' ? well?.wellType : facility?.wellType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'authorize' ? 'Authorize disposal well' : 'Disposal well'}
          </DialogTitle>
        </DialogHeader>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600 }}>{catalogLabel}</span>
            {wellType != null && <Pill label={wellType} tone="accent" />}
          </div>
          <FormRow label="Hauler display name (optional)" htmlFor="disp-name">
            <input
              id="disp-name"
              value={displayName}
              readOnly={!canManage}
              onChange={(e): void => setDisplayName(e.target.value)}
              placeholder="Nickname for this facility"
              style={fieldStyle}
            />
          </FormRow>
          {error !== null && (
            <p role="alert" style={{ ...labelStyle, color: 'var(--color-danger)' }}>
              {error}
            </p>
          )}
        </div>
        <DialogFooter>
          {mode === 'edit' && canManage && facility?.isActive === true && (
            <Button
              type="button"
              variant="outline"
              disabled={pending}
              onClick={(): void =>
                deactivate.mutate(undefined, {
                  onSuccess: () => {
                    toast.success('Disposal well deactivated');
                    close();
                  },
                  onError: () => setError('Could not deactivate. Try again.'),
                })
              }
              style={{ color: 'var(--color-danger)', borderColor: 'var(--color-danger)' }}
            >
              Deactivate
            </Button>
          )}
          <Button type="button" variant="outline" onClick={close}>
            Close
          </Button>
          {canManage && (
            <Button
              type="button"
              disabled={pending}
              onClick={handleSave}
              style={{
                backgroundColor: 'var(--color-accent-blue)',
                color: 'var(--color-on-accent)',
                borderColor: 'var(--color-accent-blue)',
              }}
            >
              {pending ? 'Saving…' : mode === 'authorize' ? 'Authorize' : 'Save'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
